import { FC, useState } from "react";
import classNames from "classnames";
import { WarningIcon } from "../../../icons/warning.icon";
import { ErrorOutlineIcon } from "../../../icons/error-outline.icon";
import { InfoIcon } from "../../../icons/info.icon";
import { CloseIcon } from "../../../icons/close.icon";
import { TertiaryButtonComponent } from "../buttons/tertiary/tertiary-button.component";
import { IconButtonComponent } from "../buttons/icon-button/icon-button.component";
import { TypographyComponent } from "../typography/typography.component";
import styles from "./inline-alert.styles.module.scss";
import { AlertVariant } from "./inline-alert.constants";
import { InlineAlertProps } from "./inline-alert.types";
import { CheckmarkIcon } from "../../../icons/checkmark.icon";

const DEFAULT_ICON_BY_VARIANT: Record<InlineAlertProps["variant"], JSX.Element> = {
  [AlertVariant.INFORMATIVE]: <InfoIcon />,
  [AlertVariant.ERROR]: <ErrorOutlineIcon />,
  [AlertVariant.WARNING]: <WarningIcon />,
  [AlertVariant.SUCCESS]: <CheckmarkIcon />,
};

export const InlineAlertComponent: FC<InlineAlertProps> = ({
  variant,
  message,
  CTALabel,
  onCTAClick = () => {},
  onCloseClick = () => {},
  showStartIcon = true,
  showCloseButton = true,
  showCTAButton = false,
  multiLine = false,
  startIcon = DEFAULT_ICON_BY_VARIANT[variant],
}) => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={classNames(
        styles.root,
        multiLine && styles.multiLineRoot,
        {
          [styles.bannerInformative]: variant === AlertVariant.INFORMATIVE,
          [styles.bannerError]: variant === AlertVariant.ERROR,
          [styles.bannerWarning]: variant === AlertVariant.WARNING,
          [styles.bannerSuccess]: variant === AlertVariant.SUCCESS,
        }
      )}
    >
      <div className={styles.container}>
        <div className={styles.messageSection}>
          {showStartIcon && startIcon}
          <div className={styles.message}>
            {typeof message === "string" ?
              <TypographyComponent styling="p-default" color="grey9">{message}</TypographyComponent>
              :
              message
            }
          </div>
        </div>
        <div className={styles.controls}>
          {showCTAButton && (
            <TertiaryButtonComponent
              size={"small"}
              onClick={onCTAClick}
              theme={"grey"}
              rootClassName={styles.ctaButton}
            >
              {CTALabel}
            </TertiaryButtonComponent>
          )}
          {showCloseButton && (
            <div className={styles.closeIcon}>
              <IconButtonComponent
                size={"small"}
                theme={"light"}
                onClick={() => {
                  setIsVisible(false);
                  onCloseClick();
                }}
                ariaLabel="close alert"
              >
                <CloseIcon />
              </IconButtonComponent>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
